import {
  NEWMAN_LOGO_BASE64,
  NEWMAN_COMPANY,
  inspectionCoverDateLine,
  type RichText,
} from '@jobos/shared';

import { cn } from '@/lib/utils';
import { useFileDrop } from '@/hooks/useFileDrop';

// Read-only preview of the generated cover sheet, shown in the editor so the
// cover is viewable like a page. Mirrors the PDF cover layout (letterhead,
// title, property address from the job, inspection date, optional building
// photo, issue summary), using the same shared logo the PDF embeds.
//
// The building photo is the one editable region: pass onPickPhoto/onDropPhoto
// to turn its box into a click + drag-drop target (the page canvas does; the
// sidebar thumbnail does not, so the thumbnail stays inert).
export interface CoverSheetProps {
  title: string;
  address: string;
  city: string;
  date: string;
  /** Job code (e.g. NC-26-433), shown after the inspection date. */
  jobCode?: string;
  issueSummary?: RichText;
  photoUrl?: string;
  onPickPhoto?: () => void;
  onDropPhoto?: (files: File[]) => void;
  isUploadingPhoto?: boolean;
}

function CoverPhoto({ photoUrl, onPickPhoto, onDropPhoto, isUploading }: {
  photoUrl?: string;
  onPickPhoto?: () => void;
  onDropPhoto?: (files: File[]) => void;
  isUploading?: boolean;
}) {
  const editable = !!onPickPhoto || !!onDropPhoto;
  const { isDragOver, dropHandlers } = useFileDrop({
    onFiles: (files) => onDropPhoto?.(files),
    disabled: !onDropPhoto || isUploading,
  });

  // Nothing to show and nothing to do — render nothing, exactly like the PDF.
  if (!photoUrl && !editable) return null;

  return (
    <div
      className={cn(
        'ins-cover-photo',
        editable && 'ins-cover-photo-editable',
        !photoUrl && 'ins-cover-photo-empty',
        isDragOver && 'ins-cover-photo-drop-over',
      )}
      {...(onDropPhoto ? dropHandlers : {})}
      {...(onPickPhoto ? { onClick: onPickPhoto, role: 'button', tabIndex: 0 } : {})}
    >
      {photoUrl ? <img src={photoUrl} alt="" /> : <span>Add a photo of the building (optional)</span>}
      {isUploading ? <div className="ins-slot-uploading" aria-label="Uploading photo" /> : null}
    </div>
  );
}

export function CoverSheet({
  title,
  address,
  city,
  date,
  jobCode,
  issueSummary,
  photoUrl,
  onPickPhoto,
  onDropPhoto,
  isUploadingPhoto,
}: CoverSheetProps) {
  // Date and job code share one line; the shared helper is what the PDF uses too.
  const dateLine = inspectionCoverDateLine(date, jobCode);
  return (
    <div className="ins-cover-page">
      <img className="ins-cover-logo" src={`data:image/jpeg;base64,${NEWMAN_LOGO_BASE64}`} alt="Newman Construction" />
      <h1 className="ins-cover-title">{title || 'Untitled report'}</h1>
      {address && <div className="ins-cover-address">{address}</div>}
      {city && <div className="ins-cover-city">{city}</div>}
      {dateLine && <div className="ins-cover-date">{dateLine}</div>}
      <CoverPhoto
        {...(photoUrl ? { photoUrl } : {})}
        {...(onPickPhoto ? { onPickPhoto } : {})}
        {...(onDropPhoto ? { onDropPhoto } : {})}
        {...(isUploadingPhoto ? { isUploading: isUploadingPhoto } : {})}
      />
      {issueSummary && issueSummary.length > 0 && (
        <div className="ins-cover-summary">
          <div className="ins-cover-summary-heading">Job Scope:</div>
          {issueSummary.map((block, i) => (
            <p key={i}>
              {block.runs.map((run, j) => {
                let node: React.ReactNode = run.text;
                if (run.underline) node = <u key={`u${j}`}>{node}</u>;
                if (run.italic) node = <em key={`i${j}`}>{node}</em>;
                if (run.bold) node = <strong key={`b${j}`}>{node}</strong>;
                return <span key={j}>{node}</span>;
              })}
            </p>
          ))}
        </div>
      )}
      <div className="ins-cover-footer">
        <div>{NEWMAN_COMPANY.name} &middot; {NEWMAN_COMPANY.address}</div>
        <div>{NEWMAN_COMPANY.phone} &middot; {NEWMAN_COMPANY.email}</div>
        <div>{NEWMAN_COMPANY.license}</div>
      </div>
    </div>
  );
}
